import { Camera } from "../render/Camera";
import type { Demo, Scene, SceneAction } from "./types";
import { makeWorld, box, ball, polygon, rng } from "./helpers";
import type { Body, CollisionEvent } from "fast-2d";
import { collisionImpactPoint, fractureBody } from "./fracture";

const IMPACT_SPEED = 13.5;

export const demolition: Demo = {
	id: "demolition",
	title: "Demolition",
	blurb:
		"A fast hammer body fractures a solid concrete block into smaller rigid fragments using the public collision event API.",
	category: "Action & Destruction",
	build(): Scene {
		const world = makeWorld();
		box(world, 0, 5.25, 28, 0.9, { isStatic: true, friction: 0.92 });
		box(world, -13.8, -0.9, 0.8, 12.0, { isStatic: true, friction: 0.8 });
		box(world, 13.8, -0.9, 0.8, 12.0, { isStatic: true, friction: 0.8 });
		box(world, 0, -6.8, 28, 0.8, { isStatic: true, friction: 0.7 });
		box(world, 4.9, 3.0, 3.4, 0.28, { isStatic: true, angle: -0.08, friction: 0.85 });

		const random = rng(0xd3e0117);
		let intact: Body | null = polygon(world, 3.55, 1.05, [
			[-1.34, -1.6],
			[1.18, -1.48],
			[1.45, -0.36],
			[1.28, 1.55],
			[-1.15, 1.62],
			[-1.48, 0.24],
		], {
			friction: 0.68,
			restitution: 0.02,
			density: 4.2,
		});
		const strikers = new Set<Body>();
		let pendingFracture: { point: [number, number]; strength: number } | null = null;
		let launched = false;
		let launchDelay = 0.36;

		const fracture = (point: [number, number], strength: number): void => {
			if (!intact || !world.bodies.includes(intact)) return;
			fractureBody(world, intact, point, strength, {
				rng: random,
				maxFragments: 11,
				minArea: 0.12,
				minEdge: 0.18,
				impulseScale: 0.11,
				maxShardSpeed: 22,
			});
			intact = null;
		};

		const launchHammer = (): void => {
			const hammer = ball(world, -8.8, 0.55, 0.86, {
				density: 30,
				friction: 0.5,
				restitution: 0.06,
				velocity: [24.5, -1.6],
				angularVelocity: -7,
			});
			strikers.add(hammer);
		};

		const onCollision = (event: CollisionEvent): void => {
			if (!intact || pendingFracture) return;
			const other = event.bodyA === intact ? event.bodyB : event.bodyB === intact ? event.bodyA : null;
			if (!other || !strikers.has(other)) return;
			const dx = other.velocity.x - intact.velocity.x;
			const dy = other.velocity.y - intact.velocity.y;
			const strength = Math.hypot(dx, dy);
			if (strength >= IMPACT_SPEED) pendingFracture = { point: collisionImpactPoint(event), strength };
		};

		world.on("collisionStart", onCollision);

		const actions: SceneAction[] = [{ label: "Launch hammer", run: launchHammer }];

		return {
			world,
			camera: new Camera(0, -1.05, 31),
			hint: "Launch the hammer to fracture the block",
			actions: () => actions,
			onBeforeStep(dt): void {
				if (pendingFracture) {
					const fractureEvent = pendingFracture;
					pendingFracture = null;
					fracture(fractureEvent.point, fractureEvent.strength);
				}
				if (launched) return;
				launchDelay -= dt;
				if (launchDelay <= 0) {
					launched = true;
					launchHammer();
				}
			},
		};
	},
};